import {
  ShellSort,
  deleteArrayItem,
  requestAnimationFrameLoop
} from "@zyzgroup/core-common";
import {
  Tween,
  Easing,
  reverseEasing,
  type EasingFunction,
  Interpolation,
  type InterpolationFunction
} from "./tween";
import Clock from "./Clock";
// import gsap from "gsap";

export type TweenValue = Record<string, any>;

export type TweenRawOptions = {
  /// 动画占用帧数
  frames?: number;
  /// 开始帧数
  delay?: number;
  easeFunction?: EasingFunction;
  interpolationFunction?: InterpolationFunction;
  repeat?: number | typeof Infinity;
  yoyo?: boolean;
  onStart?: (currentValue: TweenValue) => void;
  onUpdate?: (currentValue: TweenValue, elapsed: number) => void;
  onComplete?: (currentValue: TweenValue) => void;
};

type TimelineTweenConfig = Required<TweenRawOptions> & {
  fromValues: TweenValue;
  // number[] 代表使用插值
  toValues: TweenValue;
  tween?: Tween<TweenValue>;
};

export class Timeline {
  public tweens: TimelineTweenConfig[] = [];

  /// 每帧时间
  public frameDuration: number;
  public onGroupStart?: () => void;
  public onGroupComplete?: () => void;

  constructor(options?: {
    frameDuration?: number;
    onGroupStart?: () => void;
    onGroupComplete?: () => void;
  }) {
    this.frameDuration = options?.frameDuration || 1000;
    this.onGroupStart = options?.onGroupStart;
    this.onGroupComplete = options?.onGroupComplete;
  }

  public get count(): number {
    return this.tweens.length;
  }

  public clear(): void {
    this.tweens = [];
  }

  private sort(reverse = false) {
    ShellSort<TimelineTweenConfig>(
      this.tweens,
      (a: TimelineTweenConfig, b: TimelineTweenConfig) =>
        !reverse ? a.delay <= b.delay : a.delay > b.delay
    );
  }

  private cloneTweens(
    tweens: TimelineTweenConfig[] = this.tweens
  ): TimelineTweenConfig[] {
    const clones: TimelineTweenConfig[] = [];
    tweens.forEach((tween) => {
      const t = { ...tween };
      delete t.tween;
      t.fromValues = { ...t.fromValues };
      t.toValues = { ...t.toValues };
      clones.push(t);
    });
    return clones;
  }

  public addTween(
    fromValues: TweenValue,
    toValues: TweenValue,
    options?: TweenRawOptions
  ): this {
    const tweenConfig = {
      fromValues,
      toValues,
      frames: typeof options?.frames === "number" ? options.frames : 1,
      /// 默认顺序执行
      delay:
        typeof options?.delay === "number" ? options.delay : this.tweens.length,
      easeFunction: options?.easeFunction || Easing.Linear.None,
      interpolationFunction:
        options?.interpolationFunction || Interpolation.Linear,
      repeat: typeof options?.repeat === "number" ? options.repeat : 0,
      yoyo: options?.yoyo || false,
      onStart: options?.onStart || (() => {}),
      onUpdate: options?.onUpdate || (() => {}),
      onComplete: options?.onComplete || (() => {})
    };
    this.tweens.push(tweenConfig);
    // this.sort();
    return this;
  }

  private cancelRequestAnimationCycle = () => {};
  private runTweens(tweens: TimelineTweenConfig[]): Promise<void> {
    this.cancelRequestAnimationCycle();
    const clock = new Clock();
    const frameDuration = this.frameDuration;
    return new Promise<void>((resolve) => {
      this.onGroupStart?.();
      this.cancelRequestAnimationCycle = requestAnimationFrameLoop(
        (t: number) => {
          if (this.isPaused) {
            // tweens.forEach((t) => {
            //   if (t.tween && t.tween.isPlaying()) {
            //     t.tween.pause();
            //   }
            // });
          } else {
            const time = Math.max(0, t - this.pausedDuration);
            const duration = Math.max(
              0,
              clock.getElapsedTime() - this.pausedDuration
            );
            tweens
              .filter((t) => t.delay * frameDuration <= duration)
              .forEach((t) => {
                /// 开始的tween
                if (!t.tween) {
                  t.tween = new Tween({ ...t.fromValues })
                    .to({ ...t.toValues }, t.frames * frameDuration)
                    // .delay(t.delay * frameDuration)
                    .easing(t.easeFunction)
                    .interpolation(t.interpolationFunction)
                    .repeat(t.repeat)
                    .yoyo(t.yoyo)
                    .onStart(t.onStart)
                    .onUpdate(t.onUpdate)
                    .onComplete(t.onComplete);
                  t.tween.start(time);
                  // tween.chain(tweenBack);
                  // tweenBack.chain(tween);
                  // tween.start();
                  // tween.start("+2000");
                  // tween.start("-500");
                  // tween.stop();
                }
                // if (t.tween.isPaused()) {
                //   t.tween.resume();
                // }
                if (
                  /// 进行中的tween
                  (t.delay + t.frames * (t.repeat + 1)) * frameDuration >
                  duration
                ) {
                  t.tween.update(time);
                } else {
                  /// 结束的tween
                  if (t.tween.isPlaying()) {
                    t.tween.end();
                  }
                  deleteArrayItem(tweens, t);
                }
              });
          }
          const isContinue = !!tweens.length;
          if (!isContinue) {
            this.onGroupComplete?.();
            resolve();
          }
          return isContinue;
        }
      );
    });
  }

  public isPaused = false;
  private pausedClock = new Clock();
  private pausedDuration = 0;

  /// 注意传入的 tweens 为深拷贝新数据
  play(tweens: TimelineTweenConfig[] = this.cloneTweens(this.tweens)) {
    this.isPaused = false;
    this.pausedDuration = 0;
    this.runTweens(tweens);
  }

  reverse() {
    const reverseTweens = this.cloneTweens(this.tweens);
    let maxFrames = 0;
    reverseTweens.forEach((t) => {
      maxFrames = Math.max(maxFrames, t.delay + t.frames);
      t.easeFunction = reverseEasing(t.easeFunction);
      const temp = { ...t.toValues };
      t.toValues = { ...t.fromValues };
      t.fromValues = { ...temp };
    });
    /// 改变delay以改变执行顺序
    reverseTweens.forEach((t) => {
      t.delay = maxFrames - t.frames;
    });
    // reverseTweens.sort();
    this.play(reverseTweens);
  }

  pause() {
    this.pausedClock.start();
    this.isPaused = true;
  }

  resume() {
    this.pausedDuration += this.pausedClock.getElapsedTime();
    this.pausedClock.stop();
    this.isPaused = false;
  }
}

export default Timeline;
